"use client";

import * as React from "react";
import { CircleCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

export type ContactSubmitType = "contact" | "document_request";

export interface ContactFormValues {
  companyName: string;
  contactName: string;
  email: string;
  message: string;
  /** ハニーポット（Botトラップ）: 人間のユーザーは入力しない隠しフィールド */
  _hp?: string;
}

/** 送信完了時に表示する情報 */
export interface SubmittedInfo extends ContactFormValues {
  type: ContactSubmitType;
}

export interface ContactFormProps extends Omit<React.ComponentProps<"form">, "onError"> {
  onSuccess?: () => void;
  onError?: (message: string) => void;
}

const defaultValues: ContactFormValues = {
  companyName: "",
  contactName: "",
  email: "",
  message: "",
  _hp: "",
};

const typeLabel: Record<ContactSubmitType, string> = {
  contact: "お問い合わせ",
  document_request: "資料請求",
};

function ContactForm({ className, onSuccess, onError, ...props }: ContactFormProps) {
  const [values, setValues] = React.useState<ContactFormValues>(defaultValues);
  const [submitting, setSubmitting] = React.useState<ContactSubmitType | null>(null);
  const [submitted, setSubmitted] = React.useState<SubmittedInfo | null>(null);

  const handleChange = (field: keyof ContactFormValues) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setValues((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = (type: ContactSubmitType) => (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(type);
    void (async () => {
      try {
        const res = await fetch("/api/contact", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            companyName: values.companyName.trim(),
            contactName: values.contactName.trim(),
            email: values.email.trim(),
            message: values.message.trim() || undefined,
            type,
            _hp: values._hp ?? "",
          }),
        });
        const data = (await res.json()) as { error?: string };
        if (!res.ok) {
          onError?.(data.error ?? "送信に失敗しました。");
          return;
        }
        setSubmitted({
          companyName: values.companyName.trim(),
          contactName: values.contactName.trim(),
          email: values.email.trim(),
          message: values.message.trim(),
          type,
        });
        setValues(defaultValues);
        onSuccess?.();
        // Google Ads コンバージョン（お問い合わせ送信完了）
        if (typeof window !== "undefined" && typeof window.gtag === "function" && window.__GA_ADS_CONVERSION_SEND_TO__) {
          window.gtag("event", "conversion", { send_to: window.__GA_ADS_CONVERSION_SEND_TO__ });
        }
      } catch {
        onError?.("送信に失敗しました。");
      } finally {
        setSubmitting(null);
      }
    })();
  };

  if (submitted) {
    return (
      <div
        data-slot="contact-complete"
        className={cn(
          "flex flex-col gap-8 py-2",
          className,
        )}
      >
        <div className="flex flex-col gap-4">
          <div className="flex items-start gap-3">
            <span
              className="mt-0.5 shrink-0 text-muted-foreground/70"
              aria-hidden
            >
              <CircleCheck className="size-5" strokeWidth={1.5} />
            </span>
            <p className="font-serif text-base font-light leading-relaxed text-foreground">
              下記情報で受け付けました。メールをご確認ください
            </p>
          </div>
          <div className="border-border/50 bg-muted/5 border-l-2 pl-5 pr-4 py-4">
            <dl className="grid gap-3 text-[13px]">
              <div className="grid gap-0.5 sm:grid-cols-[minmax(7rem,auto)_1fr] sm:gap-4">
                <dt className="font-mono text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  会社名
                </dt>
                <dd className="text-foreground/90">{submitted.companyName}</dd>
              </div>
              <div className="grid gap-0.5 sm:grid-cols-[minmax(7rem,auto)_1fr] sm:gap-4">
                <dt className="font-mono text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  お名前
                </dt>
                <dd className="text-foreground/90">{submitted.contactName}</dd>
              </div>
              <div className="grid gap-0.5 sm:grid-cols-[minmax(7rem,auto)_1fr] sm:gap-4">
                <dt className="font-mono text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  メール
                </dt>
                <dd className="text-foreground/90">{submitted.email}</dd>
              </div>
              <div className="grid gap-0.5 sm:grid-cols-[minmax(7rem,auto)_1fr] sm:gap-4">
                <dt className="font-mono text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  種別
                </dt>
                <dd className="text-foreground/90">{typeLabel[submitted.type]}</dd>
              </div>
              {submitted.message ? (
                <div className="grid gap-0.5 sm:grid-cols-[minmax(7rem,auto)_1fr] sm:gap-4">
                  <dt className="font-mono text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                    内容
                  </dt>
                  <dd className="whitespace-pre-wrap text-foreground/90 leading-relaxed">
                    {submitted.message}
                  </dd>
                </div>
              ) : null}
            </dl>
          </div>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="w-fit font-normal text-muted-foreground hover:text-foreground"
          onClick={() => setSubmitted(null)}
        >
          戻る
        </Button>
      </div>
    );
  }

  return (
    <form
      data-slot="contact-form"
      className={cn("space-y-4", className)}
      onSubmit={(e) => e.preventDefault()}
      {...props}
    >
      <div className="grid gap-2">
        <Label htmlFor="contact-company">会社名</Label>
        <Input
          id="contact-company"
          type="text"
          placeholder="株式会社ABC"
          value={values.companyName}
          onChange={handleChange("companyName")}
          required
          disabled={!!submitting}
        />
      </div>
      <div className="grid gap-2">
        <Label htmlFor="contact-name">お名前</Label>
        <Input
          id="contact-name"
          type="text"
          placeholder="中本 智"
          value={values.contactName}
          onChange={handleChange("contactName")}
          required
          disabled={!!submitting}
        />
      </div>
      <div className="grid gap-2">
        <Label htmlFor="contact-email">メールアドレス</Label>
        <Input
          id="contact-email"
          type="email"
          placeholder="example@company.co.jp"
          value={values.email}
          onChange={handleChange("email")}
          required
          disabled={!!submitting}
        />
      </div>
      <div className="grid gap-2">
        <Label htmlFor="contact-message">お問い合わせ内容（任意）</Label>
        <Textarea
          id="contact-message"
          placeholder="ご質問やご要望がありましたらご記入ください。"
          value={values.message}
          onChange={handleChange("message")}
          rows={3}
          disabled={!!submitting}
        />
      </div>
      {/* ハニーポット: スクリーンリーダーとBotトラップ用。CSSで非表示にする */}
      <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", overflow: "hidden" }}>
        <label htmlFor="contact-hp">Website</label>
        <input
          id="contact-hp"
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={values._hp ?? ""}
          onChange={handleChange("_hp")}
        />
      </div>
      <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:gap-4">
        <Button
          type="button"
          size="lg"
          className="min-h-12 flex-1 font-medium touch-manipulation sm:min-h-10"
          onClick={handleSubmit("document_request")}
          disabled={!!submitting}
        >
          {submitting === "document_request" ? "送信中…" : "送信する"}
        </Button>
      </div>
    </form>
  );
}

export { ContactForm };
